import React, { useEffect, useState, useMemo, useCallback } from "react";
import AppShell from "@/components/AppShell";
import api, { apiErr } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, X as XIcon, BellRing, CheckCircle2, XCircle, Clock, Phone, Mail, Calendar as CalIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PatientPicker from "@/components/PatientPicker";

import { SERVICES, serviceLabel } from "@/components/NewVisitModal";
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function pad2(n) { return String(n).padStart(2, "0"); }
function isoDay(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function parseLocalDay(k) {
  // Parse "YYYY-MM-DD" as LOCAL date (avoid UTC drift from new Date(string))
  if (!k) return new Date();
  const [y, m, d] = k.split("-").map((x) => parseInt(x, 10));
  return new Date(y, (m || 1) - 1, d || 1);
}
function startOfMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const wd = (first.getDay() + 6) % 7; // 0=Mon
  return new Date(year, month, 1 - wd);
}

function ConfirmationBadge({ s, status }) {
  const map = {
    pending: { bg: "bg-slate-100", c: "text-slate-600", label: "Da confermare", I: Clock },
    reminder_sent: { bg: "bg-amber-100", c: "text-amber-700", label: "Promemoria inviato", I: BellRing },
    confirmed: { bg: "bg-emerald-100", c: "text-emerald-700", label: "Confermato", I: CheckCircle2 },
    confirmed_by_patient: { bg: "bg-emerald-100", c: "text-emerald-700", label: "Confermato", I: CheckCircle2 },
    needs_reschedule: { bg: "bg-orange-100", c: "text-orange-700", label: "Da richiamare", I: BellRing },
    declined: { bg: "bg-red-100", c: "text-red-700", label: "Disdetto", I: XCircle },
    cancelled: { bg: "bg-slate-200", c: "text-slate-500", label: "Annullato", I: XCircle },
    closed: { bg: "bg-slate-300", c: "text-slate-700", label: "Chiusa / Refertata", I: CheckCircle2 },
  };
  const key = (status === "closed" || status === "completed") ? "closed" : s;
  const v = map[key] || map.pending;
  return <span className={`${v.bg} ${v.c} px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 w-fit`}><v.I size={11} /> {v.label}</span>;
}

function CalendarLegend() {
  const items = [
    { bg: "bg-slate-100", c: "text-slate-600", label: "Programmata" },
    { bg: "bg-amber-100", c: "text-amber-700", label: "Promemoria inviato" },
    { bg: "bg-emerald-100", c: "text-emerald-700", label: "Confermata dal paziente" },
    { bg: "bg-orange-100", c: "text-orange-700", label: "Da richiamare" },
    { bg: "bg-slate-300", c: "text-slate-700", label: "Chiusa / Refertata" },
  ];
  return (
    <div className="flex flex-wrap gap-2 text-[10px]" data-testid="calendar-legend">
      {items.map((it) => (
        <span key={it.label} className={`${it.bg} ${it.c} px-2 py-0.5 rounded-full font-bold`}>{it.label}</span>
      ))}
    </div>
  );
}

function NewAppointmentModal({ open, onClose, defaultDate, sites, onCreated, prefillPatient }) {
  const [mode, setMode] = useState("existing");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    date: defaultDate || isoDay(new Date()), time: "09:00", duration_min: 30, service: "generale",
    patient_name: "", patient_surname: "", patient_birth: "", patient_fiscal_code: "",
    patient_phone: "", patient_email: "", patient_address: "", patient_city: "",
    privacy_contact_consent: false, site_id: "",
  });
  useEffect(() => { if (defaultDate) setForm((p) => ({ ...p, date: defaultDate })); }, [defaultDate]);
  // Reset SOLO alla transizione chiuso→aperto (con [open, sites] la fetch asincrona di sites
  // ri-eseguiva l'effect DOPO il prefill, azzerando il paziente precompilato)
  const wasOpen = React.useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setMode("existing"); setSelected(null);
      setForm((p) => ({ ...p, patient_name: "", patient_surname: "", patient_birth: "", patient_fiscal_code: "", patient_phone: "", patient_email: "", patient_address: "", patient_city: "", privacy_contact_consent: false, site_id: sites.find((s) => s.active)?.id || "" }));
    }
    if (open && !form.site_id) {
      const sid = sites.find((s) => s.active)?.id;
      if (sid) setForm((p) => (p.site_id ? p : { ...p, site_id: sid }));
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sites]);

  const handleSelect = (p) => {
    setSelected(p);
    if (p) {
      setForm((f) => ({
        ...f,
        patient_name: p.patient_name || "", patient_surname: p.patient_surname || "",
        patient_birth: p.patient_birth || "", patient_fiscal_code: p.patient_fiscal_code || "",
        patient_phone: p.patient_phone || "", patient_email: p.patient_email || "",
        privacy_contact_consent: !!p.privacy_contact_consent,
      }));
    }
  };

  // Prefill dalla cartella clinica ("Programma prossimo appuntamento"): paziente già selezionato
  useEffect(() => {
    if (open && prefillPatient && (prefillPatient.patient_name || prefillPatient.patient_fiscal_code)) {
      setMode("existing");
      setSelected(prefillPatient);
      setForm((f) => ({
        ...f,
        patient_name: prefillPatient.patient_name || "", patient_surname: prefillPatient.patient_surname || "",
        patient_birth: prefillPatient.patient_birth || "", patient_fiscal_code: prefillPatient.patient_fiscal_code || "",
        patient_phone: prefillPatient.patient_phone || "", patient_email: prefillPatient.patient_email || "",
      }));
    }
  }, [open, prefillPatient]);

  const submit = async () => {
    if (mode === "existing" && !selected) { toast.error("Seleziona un paziente o passa a 'Nuovo paziente'"); return; }
    if (!form.patient_name.trim() || !form.patient_surname.trim()) { toast.error("Nome e cognome obbligatori"); return; }
    try {
      const starts_at = `${form.date}T${form.time}:00`;
      const { data } = await api.post("/medico/appointments", {
        starts_at, duration_min: parseInt(form.duration_min, 10) || 30, service: form.service,
        patient_name: form.patient_name, patient_surname: form.patient_surname,
        patient_birth: form.patient_birth || null, patient_fiscal_code: form.patient_fiscal_code || null,
        patient_email: form.patient_email || null, patient_phone: form.patient_phone || null,
        patient_address: form.patient_address || null, patient_city: form.patient_city || null,
        privacy_contact_consent: form.privacy_contact_consent,
        site_id: form.site_id || null,
      });
      toast.success("Appuntamento creato + bozza visita");
      onCreated(data);
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="new-appt-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-extrabold text-brand-navy">Nuovo appuntamento</h3>
          <button onClick={onClose}><XIcon /></button>
        </div>

        <PatientPicker mode={mode} setMode={setMode} selected={selected} onSelect={handleSelect} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="text-xs font-bold text-slate-500">Data</label>
            <input type="date" data-testid="appt-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Ora</label>
            <input type="time" data-testid="appt-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Durata (min)</label>
            <input type="number" data-testid="appt-duration" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Tipo visita</label>
            <select data-testid="appt-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]">
              {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-bold text-slate-500">Sede / Centro *</label>
          <select data-testid="appt-site" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]">
            {sites.filter((s) => s.active).length === 0 && <option value="">— Aggiungi una sede in "Sedi" —</option>}
            {sites.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name} {s.city ? `· ${s.city}` : ""}</option>)}
          </select>
        </div>
        {(mode === "new" || selected) && (
        <>
        <div className="text-xs font-bold uppercase tracking-wider text-brand-navy mb-2">{selected ? "Paziente (autocompilato)" : "Paziente"}</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <input placeholder="Nome *" data-testid="appt-pname" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0]" />
          <input placeholder="Cognome *" data-testid="appt-psurname" value={form.patient_surname} onChange={(e) => setForm({ ...form, patient_surname: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0]" />
          <input type="date" placeholder="Data nascita" data-testid="appt-pbirth" value={form.patient_birth} onChange={(e) => setForm({ ...form, patient_birth: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0]" />
          <input placeholder="Codice fiscale" data-testid="appt-pcf" value={form.patient_fiscal_code} onChange={(e) => setForm({ ...form, patient_fiscal_code: e.target.value.toUpperCase() })} className="px-3 py-2 rounded-lg border border-[#E2E8F0] uppercase font-mono" />
          <input placeholder="Telefono" data-testid="appt-pphone" value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0]" />
          <input placeholder="Email" data-testid="appt-pemail" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0]" />
        </div>
        <label className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2 cursor-pointer text-xs text-emerald-800">
          <input data-testid="appt-consent" type="checkbox" checked={form.privacy_contact_consent} onChange={(e) => setForm({ ...form, privacy_contact_consent: e.target.checked })} className="mt-0.5" />
          <span><ShieldCheck size={11} className="inline" /> Consenso al contatto del paziente per promemoria / referto (GDPR)</span>
        </label>
        </>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Annulla</button>
          <button data-testid="confirm-new-appt" onClick={submit} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2 rounded-lg">Crea</button>
        </div>
      </div>
    </div>
  );
}

export default function Agenda() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [appts, setAppts] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("");
  const [selectedDay, setSelectedDay] = useState(isoDay(today));
  const [open, setOpen] = useState(false);
  const [defaultDate, setDefaultDate] = useState(null);
  const [prefillPatient, setPrefillPatient] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();

  // Deep-link dalla cartella clinica / assistente: ?date=YYYY-MM-DD, ?prefill=1, ?appt=<id>.
  // Deps su searchParams: funziona anche se si è già su /agenda (l'effect pulisce i params alla fine).
  useEffect(() => {
    const date = searchParams.get("date");
    const prefill = searchParams.get("prefill");
    const apptId = searchParams.get("appt");
    if (!date && !prefill && !apptId) return;
    const validDate = date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
    if (validDate) {
      const d = parseLocalDay(validDate);
      setYear(d.getFullYear()); setMonth(d.getMonth()); setSelectedDay(validDate);
    }
    if (prefill === "1") {
      try {
        const raw = window.localStorage.getItem("pv-appt-prefill");
        if (raw) { setPrefillPatient(JSON.parse(raw)); window.localStorage.removeItem("pv-appt-prefill"); }
      } catch { /* noop */ }
      setDefaultDate(validDate || isoDay(new Date()));
      setOpen(true);
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => { api.get("/medico/sites").then(({ data }) => setSites(data || [])).catch(() => {}); }, []);

  const monthBounds = useMemo(() => {
    const start = startOfMonthGrid(year, month);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [year, month]);

  const load = useCallback(async () => {
    const date_from = isoDay(monthBounds[0]);
    const date_to = isoDay(monthBounds[monthBounds.length - 1]);
    try {
      const params = { date_from, date_to };
      if (siteFilter) params.site_id = siteFilter;
      const { data } = await api.get("/medico/appointments", { params });
      setAppts(data || []);
    } catch (e) { toast.error(apiErr(e, "Errore agenda")); }
  }, [monthBounds, siteFilter]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    appts.forEach((a) => {
      const k = (a.starts_at || "").slice(0, 10);
      (m[k] = m[k] || []).push(a);
    });
    return m;
  }, [appts]);

  const dayList = (byDay[selectedDay] || []).sort((a, b) => a.starts_at.localeCompare(b.starts_at));

  const move = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y);
  };

  const sendReminder = async (a) => {
    if (!a.patient_phone && !a.patient_email) { toast.error("Paziente senza telefono o email"); return; }
    try { await api.post(`/medico/appointments/${a.id}/send-reminder`); toast.success("Promemoria 24h inviato (mock)"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const simulate = async (a, response) => {
    try { await api.post(`/medico/appointments/${a.id}/simulate-response`, { response }); toast.success(response === "yes" ? "Conferma ricevuta + notifica al medico" : "Disdetta ricevuta"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const movePrompt = async (a) => {
    const newDate = window.prompt("Sposta a (formato YYYY-MM-DDTHH:MM):", a.starts_at.slice(0, 16));
    if (!newDate) return;
    try {
      const iso = newDate.length === 16 ? `${newDate}:00` : newDate;
      await api.patch(`/medico/appointments/${a.id}`, { starts_at: iso });
      toast.success("Visita spostata + audit log");
      load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  return (
    <AppShell
      title="Agenda"
      subtitle="Il tuo calendario personale. Visibile solo a te."
      rightSlot={<button data-testid="open-new-appt" onClick={() => { setDefaultDate(selectedDay); setOpen(true); }} className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-4 py-2.5 rounded-lg flex items-center gap-2"><Plus size={16} /> Nuovo appuntamento</button>}
    >
      {sites.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="site-filter-bar">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Sedi:</span>
          <button data-testid="site-filter-all" onClick={() => setSiteFilter("")} className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${siteFilter === "" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-[#E2E8F0]"}`}>Tutte</button>
          {sites.filter((s) => s.active).map((s) => (
            <button key={s.id} data-testid={`site-filter-${s.id}`} onClick={() => setSiteFilter(s.id)} className={`px-3 py-1 rounded-full text-xs font-bold border-2 flex items-center gap-1.5 transition ${siteFilter === s.id ? "text-white" : "bg-white text-slate-700"}`}
              style={siteFilter === s.id ? { backgroundColor: s.color, borderColor: s.color } : { borderColor: s.color }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }}></span>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="month-prev" onClick={() => move(-1)} className="p-2 rounded-lg hover:bg-slate-100"><ChevronLeft /></button>
            <div className="text-xl font-extrabold text-brand-navy">{MONTHS[month]} {year}</div>
            <button data-testid="month-next" onClick={() => move(1)} className="p-2 rounded-lg hover:bg-slate-100"><ChevronRight /></button>
          </div>
          <div className="mb-2"><CalendarLegend /></div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold text-slate-500 mb-1">
            {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="month-grid">
            {monthBounds.map((d, i) => {
              const k = isoDay(d);
              const inMonth = d.getMonth() === month;
              const isToday = isoDay(d) === isoDay(today);
              const isSelected = k === selectedDay;
              const dayAppts = byDay[k] || [];
              const count = dayAppts.length;
              const closedCount = dayAppts.filter((a) => a.status === "closed" || a.status === "completed").length;
              const openCount = count - closedCount;
              return (
                <button
                  key={i} data-testid={`day-${k}`} onClick={() => setSelectedDay(k)}
                  className={`min-h-[60px] rounded-lg text-sm flex flex-col items-stretch p-1.5 transition border-2
                    ${isSelected ? "border-brand-azure bg-brand-azure/10" : "border-transparent hover:border-brand-azure/40"}
                    ${inMonth ? "text-slate-700" : "text-slate-300"}`}
                >
                  <div className={`text-right font-bold ${isToday ? "text-brand-orange" : ""}`}>{d.getDate()}</div>
                  {count > 0 && (
                    <div className="mt-auto flex items-center justify-center gap-1">
                      {openCount > 0 && <>
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-azure" />
                        <span className="text-[10px] font-bold text-brand-azure">{openCount}</span>
                      </>}
                      {closedCount > 0 && <>
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                        <span className="text-[10px] font-bold text-slate-500">{closedCount}</span>
                      </>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <CalIcon className="text-brand-azure" size={18} />
            <div className="font-extrabold text-brand-navy">{selectedDay ? parseLocalDay(selectedDay).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }) : "—"}</div>
          </div>
          {dayList.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">Nessun appuntamento</div>
          ) : (
            <ul className="space-y-2" data-testid="day-appointments">
              {dayList.map((a) => {
                const site = sites.find((s) => s.id === a.site_id);
                const color = site?.color || "#94A3B8";
                return (
                <li key={a.id} data-testid={`appt-${a.id}`} className="border border-[#E2E8F0] rounded-lg overflow-hidden" style={{ borderLeft: `4px solid ${color}` }}>
                  <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-500">{a.starts_at.slice(11, 16)} · {a.duration_min}min · {serviceLabel(a.service)}</div>
                      <div className="font-extrabold text-brand-navy">{a.patient_full_name}</div>
                      {a.site_name && <div className="text-[11px] font-bold mt-0.5 flex items-center gap-1" style={{ color }}><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }}></span>{a.site_name}</div>}
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                        {a.patient_phone && <span className="flex items-center gap-1"><Phone size={10} /> {a.patient_phone}</span>}
                        {a.patient_email && <span className="flex items-center gap-1"><Mail size={10} /> {a.patient_email}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    <ConfirmationBadge s={a.confirmation_status || a.confirmation} status={a.status} />
                    <div className="flex flex-wrap gap-1">
                      {a.confirmation !== "confirmed" && a.confirmation !== "declined" && (
                        <button data-testid={`send-rem-${a.id}`} onClick={() => sendReminder(a)} className="text-[10px] font-bold bg-amber-100 hover:bg-amber-200 text-amber-700 px-2 py-1 rounded">Invia promemoria 24h</button>
                      )}
                      {(a.confirmation === "reminder_sent" || a.confirmation === "pending") && (
                        <>
                          <button data-testid={`sim-yes-${a.id}`} onClick={() => simulate(a, "yes")} className="text-[10px] font-bold bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-2 py-1 rounded">Simula SI</button>
                          <button data-testid={`sim-no-${a.id}`} onClick={() => simulate(a, "no")} className="text-[10px] font-bold bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded">Simula NO</button>
                        </>
                      )}
                      <button data-testid={`move-${a.id}`} onClick={() => movePrompt(a)} className="text-[10px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded">Sposta</button>
                      <button data-testid={`open-visit-${a.id}`} onClick={() => nav(`/visite/${a.visit_id}`)} className="text-[10px] font-bold bg-brand-navy hover:bg-brand-navyDark text-white px-2 py-1 rounded">Apri visita</button>
                    </div>
                  </div>
                  </div>
                </li>
              );})}
            </ul>
          )}
        </div>
      </div>

      <NewAppointmentModal open={open} sites={sites} onClose={() => { setOpen(false); setPrefillPatient(null); }} defaultDate={defaultDate} prefillPatient={prefillPatient} onCreated={(data) => { setOpen(false); setPrefillPatient(null); load(); }} />
    </AppShell>
  );
}
